<template>
  <draggable
    v-model="value"
    tag="ul"
    class="flex flex-col items-center gap-y-4"
    item-key="id"
  >
    <template #item="{element, index}">
      <div class="bg-success-green-10 w-full rounded flex items-center">
        <div class="draggable-area" />
        <multi-select
          v-model="element.rankingType"
          class="py-2 px-3 grow"
          :label="$t('rankingPicker.rankingStatementType.label')"
          :placeholder="$t('rankingPicker.rankingStatementType.label')"
          :options="fetchTypes"
        />
        <close-icon
          class="flex-none w-6 h-6 cursor-pointer stroke-none"
          @click.prevent="deleteRanking(index)"
        />
      </div>
    </template>
    <template #footer>
      <button class="text-sm px-2 h-8 mt-2" @click.prevent="addRanking">
        {{ $t('rankingPicker.addRanking') }}
      </button>
      <span v-if="error" class="text-sm text-red">{{ error }}</span>
    </template>
  </draggable>
</template>

<script lang="ts" setup>
import draggable from 'vuedraggable';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { onMounted } from 'vue';
import CloseIcon from '@images/remove.svg?component';
import { enumOptionsProvider } from '../../table/hooks/provider';
import type { RankingStatement } from '@/entities/Entities';
import MultiSelect from '../../Multiselect.vue';

const { t } = useI18n();

interface Props {
  modelValue: Array<Pick<RankingStatement, 'id' | 'rankingType'>>;
  label?: string;
  disabled?: boolean;
  error?: string;
}

const emit = defineEmits(['update:modelValue']);
const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
});

const value = useVModel(props, 'modelValue', emit, { passive: true });

const addRanking = () => {
  value.value.push({
    id: Math.random().toString(36),
    rankingType: '',
  });
};

const deleteRanking = (index: number) => {
  value.value.splice(index, 1);
};

const fetchTypes = enumOptionsProvider('/enums/RankingType', (v) => ({
  value: v,
  label: t(`rankingPicker.rankingStatementType.${v}`),
}));

onMounted(() => {
  fetchTypes();
});
</script>

<style lang="scss" scoped>
.draggable-area {
  @apply flex-none self-stretch w-6 relative bg-success-green-100 p-2 rounded-l;

  &:after {
    @apply absolute z-20 bg-white opacity-50;
    content: "";
    inset: 0.25rem;
    mask-image: url("/draggable.svg");
    mask-repeat: space;
    mask-size: 100% auto;
  }

}
</style>
